import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EngForm } from "@server/submissions/schema";
import type { FollowUpAction, IncidentForm, Responder } from "@server/incidents/schema";
import type { AdminsView } from "@server/admins/types";
import type { IncidentView, ResponderConfig } from "@server/incidents/types";
import type { SeveritySuggestion } from "@server/incidents/severity";
import type { TicketType } from "@server/submissions/types";
import type { CompanyAutofill } from "@server/lookups/types";
import type { IncidentRow, TicketRow } from "@server/reporting/types";
import type { ListsUpdate } from "@server/lists/schema";
import type { StoredFormLists } from "@server/lists/types";
import type { Team } from "@server/rules/features";
import { routeTeam } from "@server/rules/routing";
import { contentTypeFor, type FileRef } from "@server/uploads/files";
import { clearDraft } from "../draft";
import { WORKING_STATUSES, type Me, type SubmissionStatus, type SubmissionSummary, type SubmissionView } from "./types";

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    // What the server found wrong, when it says (a 400 from the form checks).
    public readonly issues: readonly string[] = [],
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: { method?: string; body?: unknown; signal?: AbortSignal }): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: init?.method ?? "GET",
    headers: init?.body === undefined ? undefined : { "Content-Type": "application/json" },
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: "same-origin",
    signal: init?.signal,
  });
  if (response.status === 401) {
    // The server's Auth.js config sends sign-in to /login, which this app serves.
    window.location.href = `/login?callbackUrl=${encodeURIComponent(window.location.href)}`;
    return new Promise<T>(() => undefined);
  }
  const text = await response.text();
  const body = text ? (JSON.parse(text) as { message?: string; issues?: { message?: string }[] }) : {};
  if (!response.ok) {
    const issues = (body.issues ?? []).flatMap((issue) => (issue.message ? [issue.message] : []));
    throw new ApiError(body.message ?? `Request failed (${response.status})`, response.status, issues);
  }
  return body as T;
}

// Auth.js only accepts a POST with a CSRF token, so the page builds and submits a form.
export async function postAuthForm(action: string, callbackUrl: string): Promise<void> {
  const { csrfToken } = (await (await fetch("/api/auth/csrf")).json()) as { csrfToken: string };
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  for (const [name, value] of Object.entries({ csrfToken, callbackUrl })) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

// Ends the session and drops the unsent draft, which may hold customer details.
export function signOut(): Promise<void> {
  clearDraft();
  return postAuthForm("/api/auth/signout", `${window.location.origin}/login`);
}

// Asks the server for a one-off upload URL, then sends the file straight to storage.
export async function uploadFile(file: File): Promise<FileRef> {
  const contentType = contentTypeFor(file);
  const upload = await request<{ key: string; name: string; url: string; headers: Record<string, string> }>("/uploads", {
    method: "POST",
    body: { name: file.name, contentType, size: file.size },
  });
  const response = await fetch(upload.url, { method: "PUT", headers: upload.headers, body: file });
  if (!response.ok) {
    throw new ApiError("The upload failed. Try again.", response.status);
  }
  return { key: upload.key, name: upload.name, contentType, size: file.size };
}

// Asks the server what Vestd knows about the company in the URL. Any failure ends as "no suggestion".
export async function lookupCompany(appUrl: string, signal: AbortSignal): Promise<CompanyAutofill | undefined> {
  const { result } = await request<{ result: CompanyAutofill }>("/company-lookup", { method: "POST", body: { appUrl }, signal });
  return result;
}

// Asks the server for a severity for the incident being written. Any failure ends as "no suggestion".
export async function suggestSeverity(input: { title: string; description: string; companiesImpacted: string; types: readonly string[] }, signal: AbortSignal): Promise<SeveritySuggestion | undefined> {
  const { result } = await request<{ result: Partial<SeveritySuggestion> }>("/incident-severity", { method: "POST", body: input, signal });
  return result.severity ? { severity: result.severity, reason: result.reason ?? "" } : undefined;
}

const keys = {
  me: ["me"] as const,
  mine: ["submissions"] as const,
  one: (id: string) => ["submission", id] as const,
  admin: (status: SubmissionStatus) => ["admin", status] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => request<Me>("/me"), staleTime: Infinity });

export const useTicketsChannel = (): string => `#${useMe().data?.ticketsChannel ?? "prod-engineering-tickets"}`;

export const useMySubmissions = () =>
  useQuery({ queryKey: keys.mine, queryFn: async () => (await request<{ submissions: SubmissionSummary[] }>("/submissions")).submissions });

export const useAdminSubmissions = (status: SubmissionStatus) =>
  useQuery({
    queryKey: keys.admin(status),
    queryFn: async () => (await request<{ submissions: SubmissionSummary[] }>(`/admin/submissions?status=${status}`)).submissions,
  });

export const useSubmission = (id: string) =>
  useQuery({
    queryKey: keys.one(id),
    queryFn: () => request<SubmissionView>(`/submissions/${encodeURIComponent(id)}`),
    refetchInterval: (query) => (query.state.data && WORKING_STATUSES.includes(query.state.data.status) ? 2000 : false),
  });

// Each action answers with the updated submission, which goes straight into the cache.
function useAction<V>(path: (id: string, variables: V) => string, body?: (variables: V) => unknown) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, variables }: { id: string; variables: V }) =>
      request<SubmissionView>(path(id, variables), { method: "POST", body: body?.(variables) }),
    onSuccess: (view) => {
      client.setQueryData(keys.one(view.id), view);
      void client.invalidateQueries({ queryKey: keys.mine });
    },
  });
}

export const useCreateSubmission = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (form: EngForm) => request<SubmissionView>("/submissions", { method: "POST", body: form }),
    onSuccess: (view) => {
      client.setQueryData(keys.one(view.id), view);
      void client.invalidateQueries({ queryKey: keys.mine });
    },
  });
};

export const useAcknowledge = () => useAction<void>((id) => `/submissions/${encodeURIComponent(id)}/acknowledge`);
export const useOverride = () =>
  useAction<{ reason: string }>(
    (id) => `/submissions/${encodeURIComponent(id)}/override`,
    (variables) => variables,
  );
export const useClose = () => useAction<void>((id) => `/submissions/${encodeURIComponent(id)}/close`);
export const useSetTicketType = () =>
  useAction<{ ticketType: TicketType }>(
    (id) => `/submissions/${encodeURIComponent(id)}/type`,
    (variables) => variables,
  );
export const useRetry = () => useAction<void>((id) => `/submissions/${encodeURIComponent(id)}/retry`);

const incidentKey = (id: string) => ["incident", id] as const;
const RESPONDERS_KEY = ["incident-responders"] as const;

export const useCreateIncident = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (form: IncidentForm) => request<IncidentView>("/incidents", { method: "POST", body: form }),
    onSuccess: (view) => client.setQueryData(incidentKey(view.id), view),
  });
};

export const useIncident = (id: string) =>
  useQuery({
    queryKey: incidentKey(id),
    queryFn: () => request<IncidentView>(`/incidents/${encodeURIComponent(id)}`),
    refetchInterval: (query) => (query.state.data?.status === "INCIDENT_QUEUED" ? 2000 : false),
  });

export const useRetryIncident = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<IncidentView>(`/incidents/${encodeURIComponent(id)}/retry`, { method: "POST" }),
    onSuccess: (view) => client.setQueryData(incidentKey(view.id), view),
  });
};

export const useIncidentFollowUp = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: FollowUpAction }) =>
      request<IncidentView>(`/incidents/${encodeURIComponent(id)}/follow-up`, { method: "POST", body: action }),
    onSuccess: (view) => client.setQueryData(incidentKey(view.id), view),
  });
};

export const useResponders = () =>
  useQuery({ queryKey: RESPONDERS_KEY, queryFn: () => request<ResponderConfig>("/admin/incident-responders") });

export const useSaveResponders = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: { responders: Responder[]; version: number }) =>
      request<ResponderConfig>("/admin/incident-responders", { method: "PUT", body: update }),
    onSuccess: (saved) => client.setQueryData(RESPONDERS_KEY, saved),
  });
};

const ADMINS_KEY = ["admins"] as const;

// Who the admins are: the ones set in the code, and the ones added on the admin page.
export const useAdmins = () => useQuery({ queryKey: ADMINS_KEY, queryFn: () => request<AdminsView>("/admin/admins") });

export const useSaveAdmins = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: { emails: string[]; version: number }) => request<AdminsView>("/admin/admins", { method: "PUT", body: update }),
    onSuccess: (saved) => client.setQueryData(ADMINS_KEY, saved),
  });
};

const LISTS_KEY = ["form-lists"] as const;

// The Feature, Business unit and Pricing plan lists, with retired values marked. The form offers only the active ones.
export const useFormLists = () =>
  useQuery({ queryKey: LISTS_KEY, queryFn: () => request<StoredFormLists>("/form-lists"), staleTime: 30_000 });

// The team a Feature routes to, by the lists as they are now (Platform until they have loaded).
// The team a ticket routes to, by the lists as they are now and the team triage suggested.
export const useTeamFor = (feature: string | undefined, triageTeam?: Team | null): Team => routeTeam(feature, triageTeam, useFormLists().data?.features).team;

export const useSaveFormLists = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: ListsUpdate) => request<StoredFormLists>("/admin/form-lists", { method: "PUT", body: update }),
    onSuccess: (saved) => client.setQueryData(LISTS_KEY, saved),
  });
};

// Reports. The ticket rows hold no ticket text (it is fetched a page at a time), so loading every row stays small.
// They are fetched again on every visit and each minute, so a ticket raised a moment ago turns up without a page refresh.
export const useTicketRows = () =>
  useQuery({
    queryKey: ["report-tickets"],
    queryFn: () => request<{ rows: TicketRow[]; generatedAt: string }>("/reports/tickets"),
    staleTime: 0,
    refetchInterval: 60_000,
  });

export const useIncidentRows = () =>
  useQuery({
    queryKey: ["report-incidents"],
    queryFn: async () => (await request<{ rows: IncidentRow[] }>("/reports/incidents")).rows,
    staleTime: 0,
    refetchInterval: 60_000,
  });

// Submissions triage said needed no ticket that were closed without one. Only the AI triage tab asks for them.
export const useNoTicketRows = (enabled: boolean) =>
  useQuery({
    queryKey: ["report-no-ticket"],
    queryFn: async () => (await request<{ rows: TicketRow[] }>("/reports/no-ticket")).rows,
    enabled,
    staleTime: 0,
    refetchInterval: 60_000,
  });

export async function fetchTicketDetails(ids: readonly string[]): Promise<Record<string, string>> {
  return (await request<{ details: Record<string, string> }>("/reports/tickets/details", { method: "POST", body: { ids } })).details;
}

export const useTicketDetails = (ids: readonly string[]) =>
  useQuery({
    queryKey: ["report-details", ids.join(",")],
    queryFn: () => fetchTicketDetails(ids),
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
  });
